// Payroll - Approval & Status Tab
import React, { useState, useEffect } from 'react';
import { hasAnyRole } from '../../utils/roleHelper';

const STATUS = {
  draft: { bg: '#f1f5f9', text: '#475569', label: 'Draft' },
  reviewed: { bg: '#ede9fe', text: '#5b21b6', label: 'Reviewed (L1)' },
  approved: { bg: '#dcfce7', text: '#166534', label: 'Approved (L2)' },
  finalized: { bg: '#fef3c7', text: '#92400e', label: 'Finalized & Locked' },
  paid: { bg: '#cffafe', text: '#155e75', label: 'Paid' },
};

const ApprovalTab = ({ api, user, armyTypeId, armyTypeName }) => {
  const [payrolls, setPayrolls] = useState([]);
  const [selected, setSelected] = useState(null);
  const [items, setItems] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [batches, setBatches] = useState([]);

  const isBudgetOffice = hasAnyRole(user, ['admin', 'finance_officer', 'finance', 'payroll_officer']);
  const isDirectorGeneral = hasAnyRole(user, ['admin', 'director_general', 'finance_director']);
  const isAccountantGeneral = hasAnyRole(user, ['admin', 'accountant_general', 'finance_director']);

  useEffect(() => { fetchPayrolls(); }, [armyTypeId]);

  const fetchPayrolls = async () => {
    try {
      const res = await api.get(`/payrolls?army_type_id=${armyTypeId}`);
      const all = res?.payrolls || res?.data?.payrolls || (Array.isArray(res) ? res : []);
      setPayrolls(all.filter(p => {
        const forceId = p.payroll_group?.army_type_id || p.army_type_id;
        return !armyTypeId || String(forceId) === String(armyTypeId);
      }));
    } catch (e) { console.error('Error fetching payrolls:', e); }
  };

  const selectPayroll = async (p) => {
    setSelected(p); setMsg({ text: '', type: '' });
    try {
      const res = await api.get(`/payrolls/${p.id}`);
      setItems(res?.items || res?.data?.items || []);
      setApprovals(res?.approvals || res?.data?.approvals || []);
      setSummary(res?.summary || res?.data?.summary || null);

      const bRes = await api.get(`/payrolls/${p.id}/payment_batches`);
      setBatches(bRes?.payment_batches || bRes?.data?.payment_batches || []);
    } catch (e) { console.error(e); }
  };

  const formatApiError = (res, fallback) => {
    if (!res) return fallback;
    const d = res.data || res;
    if (d.error) return d.error;
    if (Array.isArray(d.errors)) return d.errors.join(', ');
    if (typeof d.errors === 'string') return d.errors;
    if (typeof d.errors === 'object' && d.errors !== null) {
      return Object.values(d.errors).flat().join(', ');
    }
    return fallback;
  };

  const doApprove = async (stage) => {
    if (!remarks && stage !== 'pay') return setMsg({ text: 'Please add remarks before approving', type: 'error' });
    setLoading(true);
    try {
      const res = await api.post(`/payrolls/${selected.id}/approve`, { stage, remarks });
      if (res?.response && !res.response.ok) {
        const err = formatApiError(res, 'Approval failed');
        setMsg({ text: `❌ ${err}`, type: 'error' });
      } else {
        setMsg({ text: `✅ Approval completed successfully`, type: 'success' });
        setRemarks('');
        fetchPayrolls();
        selectPayroll(selected);
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Approval failed'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const doReject = async () => {
    if (!remarks) return setMsg({ text: 'Rejection reason is required', type: 'error' });
    if (!confirm('Reject and send back to Draft?')) return;
    setLoading(true);
    try {
      const res = await api.post(`/payrolls/${selected.id}/reject`, { remarks, stage: selected.status });
      if (res?.response && !res.response.ok) {
        const err = formatApiError(res, 'Rejection failed');
        setMsg({ text: `❌ ${err}`, type: 'error' });
      } else {
        setMsg({ text: '✅ Payroll rejected and sent back to Draft', type: 'success' });
        setRemarks('');
        fetchPayrolls();
        selectPayroll(selected);
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Rejection failed'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const doGenerateFiles = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/payrolls/${selected.id}/generate_payment_files`);
      if (res?.response && !res.response.ok) {
        const err = formatApiError(res, 'Failed to generate files');
        setMsg({ text: `❌ ${err}`, type: 'error' });
      } else {
        setMsg({ text: '✅ Payment files generated successfully', type: 'success' });
        selectPayroll(selected);
      }
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Failed to generate files'}`, type: 'error' });
    } finally { setLoading(false); }
  };

  const handleDownloadFile = async (batchId, type) => {
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token') || sessionStorage.getItem('token');
      const res = await fetch(`/api/v1/payrolls/${selected.id}/download_payment_file?batch_id=${batchId}&type=${type}`, {
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });

      if (!res.ok) {
        throw new Error(`Failed to download payment file (HTTP ${res.status})`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ext = type === 'csv' ? 'csv' : (type === 'letter' ? 'html' : 'txt');
      a.download = `Payment_Batch_${batchId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setMsg({ text: `❌ ${e.message || 'Error downloading payment file'}`, type: 'error' });
    }
  };

  const fmt = (n) => `$${parseFloat(n || 0).toFixed(2)}`;
  const fmtDate = (d) => new Date(d).toLocaleString();

  const actionColors = {
    verify: '#3b82f6', approve: '#059669', finalize: '#f59e0b', reject: '#ef4444', pay: '#06b6d4'
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '260px minmax(0, 1fr)', gap: 20, minHeight: 500, width: '100%', maxWidth: '100%' }}>
      {/* Left: Payroll List */}
      <div style={{ minWidth: 0 }}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', background: '#1b4332', color: '#fff' }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Payroll Runs</h3>
            <p style={{ margin: '2px 0 0', fontSize: 11, opacity: 0.8 }}>{armyTypeName}</p>
          </div>
          {payrolls.length === 0 && (
            <p style={{ padding: 20, color: '#9ca3af', fontSize: 13, margin: 0 }}>No payroll runs found. Generate one in the Processing tab.</p>
          )}
          {payrolls.map(p => {
            const s = STATUS[p.status] || STATUS.draft;
            return (
              <div key={p.id} onClick={() => selectPayroll(p)}
                style={{
                  padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9',
                  background: selected?.id === p.id ? '#f0fdf4' : '#fff',
                  borderLeft: selected?.id === p.id ? '3px solid #1b4332' : '3px solid transparent'
                }}>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{p.payroll_period}</div>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{p.payroll_group?.name}</div>
                <span style={{ marginTop: 4, display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: s.bg, color: s.text }}>{s.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Right: Detail */}
      <div style={{ minWidth: 0, width: '100%' }}>
        {!selected ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <div style={{ textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: 40 }}>👈</div>
              <p>Select a payroll run to view details</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Header */}
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 800 }}>Payroll — {selected.payroll_period}</h2>
                  <div style={{ fontSize: 13, color: '#6b7280' }}>{selected.payroll_group?.name}</div>
                </div>
                <span style={{
                  padding: '5px 16px', borderRadius: 20, fontWeight: 800, fontSize: 13,
                  background: STATUS[selected.status]?.bg, color: STATUS[selected.status]?.text
                }}>
                  {STATUS[selected.status]?.label}
                </span>
              </div>

              {/* Summary */}
              {summary && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 12, marginTop: 16 }}>
                  {[
                    { label: 'Personnel', value: summary.headcount, color: '#374151' },
                    { label: 'Base Salary', value: fmt(summary.total_base), color: '#1d4ed8' },
                    { label: 'Allowances', value: fmt(summary.total_allowances), color: '#059669' },
                    { label: 'Deductions', value: fmt(summary.total_deductions), color: '#dc2626' },
                    { label: 'Net Payable', value: fmt(summary.total_net), color: '#1b4332' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#f8fafc', borderRadius: 6, padding: '10px 14px', textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600 }}>{s.label}</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: s.color, marginTop: 2 }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Approval Actions */}
            {!selected.locked && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Approval Actions</h3>
                {msg.text && (
                  <div style={{
                    marginBottom: 12, padding: '8px 12px', borderRadius: 5, fontSize: 13,
                    background: msg.type === 'error' ? '#fee2e2' : '#dcfce7',
                    color: msg.type === 'error' ? '#991b1b' : '#166534',
                    border: `1px solid ${msg.type === 'error' ? '#fca5a5' : '#86efac'}`
                  }}>
                    {msg.text}
                  </div>
                )}
                <textarea value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Add remarks (required for approval/rejection)"
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, minHeight: 64, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10 }} />
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {/* Level 1: Finance Budget Office Review */}
                  {isBudgetOffice && selected.status === 'draft' && (
                    <button onClick={() => doApprove('finance_officer')} disabled={loading}
                      style={{ padding: '9px 18px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      ✅ L1: Review & Verify (Budget Office)
                    </button>
                  )}
                  {/* Level 2: MoF Director General Approval */}
                  {isDirectorGeneral && selected.status === 'reviewed' && (
                    <button onClick={() => doApprove('director_general')} disabled={loading}
                      style={{ padding: '9px 18px', background: '#059669', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      ✔️ L2: Approve (Director General)
                    </button>
                  )}
                  {/* Level 3: Accountant General Final Approval & Lock */}
                  {isAccountantGeneral && selected.status === 'approved' && (
                    <button onClick={() => doApprove('accountant_general')} disabled={loading}
                      style={{ padding: '9px 18px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      🔒 L3: Final Approve & Lock (Accountant General)
                    </button>
                  )}
                  {/* Mark as Paid */}
                  {isBudgetOffice && selected.status === 'finalized' && (
                    <button onClick={() => doApprove('pay')} disabled={loading}
                      style={{ padding: '9px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      💳 Mark Bank Payment Complete
                    </button>
                  )}
                  {/* Reopen Finalized Payroll */}
                  {isAccountantGeneral && ['finalized', 'paid'].includes(selected.status) && (
                    <button onClick={async () => {
                      const reason = prompt("Reason for reopening this finalized payroll?");
                      if (!reason) return;
                      setLoading(true);
                      try {
                        const res = await api.post(`/payrolls/${selected.id}/reopen`, { reason });
                        if (res?.response && !res.response.ok) {
                          setMsg({ text: `❌ ${res.data?.error || 'Failed to reopen payroll'}`, type: 'error' });
                        } else {
                          setMsg({ text: '✅ Payroll reopened and unlocked for editing', type: 'success' });
                          fetchPayrolls();
                          selectPayroll(selected);
                        }
                      } catch (e) { setMsg({ text: `❌ ${e.message}`, type: 'error' }); }
                      finally { setLoading(false); }
                    }} disabled={loading}
                      style={{ padding: '9px 18px', background: '#ea580c', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      🔓 Reopen Payroll (Unlock)
                    </button>
                  )}
                  {/* Reject */}
                  {(isBudgetOffice || isDirectorGeneral || isAccountantGeneral) && ['draft', 'reviewed', 'approved'].includes(selected.status) && (
                    <button onClick={doReject} disabled={loading}
                      style={{ padding: '9px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                      ❌ Reject to Draft
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Payment Files */}
            {['finalized', 'paid'].includes(selected.status) && isBudgetOffice && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px', marginBottom: 16 }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Payment Files</h3>
                <button onClick={doGenerateFiles} disabled={loading}
                  style={{ marginBottom: 12, padding: '9px 18px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>
                  📄 Generate Bank Transfer Files
                </button>
                {batches.map(b => (
                  <div key={b.id} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 6, marginTop: 8 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Batch: {b.batch_number}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={() => handleDownloadFile(b.id, 'tsv')}
                        style={{ padding: '5px 12px', background: '#1b4332', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        📊 Download TSV (Bank)
                      </button>
                      <button onClick={() => handleDownloadFile(b.id, 'csv')}
                        style={{ padding: '5px 12px', background: '#059669', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        📈 Download CSV (Excel)
                      </button>
                      <button onClick={() => handleDownloadFile(b.id, 'letter')}
                        style={{ padding: '5px 12px', background: '#374151', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>
                        📄 Download Auth Letter
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Personnel Breakdown */}
            {items.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 16, width: '100%', maxWidth: '100%' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Personnel Detailed Master Payroll Breakdown</h3>
                  <span style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Showing {items.length} personnel records</span>
                </div>
                <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', display: 'block', WebkitOverflowScrolling: 'touch' }}>
                  <table style={{ width: 'max-content', minWidth: 1450, borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        {['S/N', '(Pay Grade)', 'Name', 'Phone', 'Bank Name', 'Account No.', 'Ururka(Organization)', 'Salary', 'G.Xil', 'G.Farsamo', 'G.Hawl', 'Gross Salary', 'MM', 'Bolo', 'Health', 'Net Pay', 'Status'].map(h => (
                          <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#374151', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, i) => {
                        const p = item.army_personnel || {};
                        const details = item.details || {};
                        const allowances = details.allowances || [];
                        const deductions = details.deductions || [];

                        const getAllow = (keywords) => {
                          const matched = allowances.filter(a => keywords.some(k => (a.allowance_type || a.name || '').toLowerCase().includes(k)));
                          return matched.reduce((s, a) => s + parseFloat(a.calculated_amount || a.amount || 0), 0);
                        };

                        const getDeduct = (keywords) => {
                          const matched = deductions.filter(d => keywords.some(k => (d.deduction_type || d.name || '').toLowerCase().includes(k)));
                          return matched.reduce((s, d) => s + parseFloat(d.calculated_amount || d.amount || 0), 0);
                        };

                        const gXil = getAllow(['xil', 'duty', 'rank']);
                        const gFarsamo = getAllow(['farsamo', 'tech']);
                        const gHawl = getAllow(['hawl', 'operat', 'field']);
                        const mm = getDeduct(['mm', 'milit', 'mortg']);
                        const bolo = getDeduct(['bolo', 'fine', 'penalt']);
                        const health = getDeduct(['health', 'insur', 'medic']);

                        return (
                          <tr key={item.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                            <td style={{ padding: '9px 14px', fontWeight: 700, color: '#64748b', whiteSpace: 'nowrap' }}>{i + 1}</td>
                            <td style={{ padding: '9px 14px', fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>{p.rank || 'Grade-1'}</td>
                            <td style={{ padding: '9px 14px', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{p.full_name}</td>
                            <td style={{ padding: '9px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{p.phone || 'N/A'}</td>
                            <td style={{ padding: '9px 14px', color: '#1e293b', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.bank_name || 'Bank'}</td>
                            <td style={{ padding: '9px 14px', color: '#0284c7', fontWeight: 600, whiteSpace: 'nowrap' }}>{p.bank_account || `PBP-${p.army_id || item.id}`}</td>
                            <td style={{ padding: '9px 14px', color: '#475569', whiteSpace: 'nowrap' }}>{p.department?.name || p.army_type?.name || 'N/A'}</td>
                            <td style={{ padding: '9px 14px', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(item.base_salary)}</td>
                            <td style={{ padding: '9px 14px', color: '#059669', whiteSpace: 'nowrap' }}>+{fmt(gXil)}</td>
                            <td style={{ padding: '9px 14px', color: '#059669', whiteSpace: 'nowrap' }}>+{fmt(gFarsamo)}</td>
                            <td style={{ padding: '9px 14px', color: '#059669', whiteSpace: 'nowrap' }}>+{fmt(gHawl)}</td>
                            <td style={{ padding: '9px 14px', fontWeight: 700, color: '#1d4ed8', whiteSpace: 'nowrap' }}>{fmt(item.gross_salary)}</td>
                            <td style={{ padding: '9px 14px', color: '#dc2626', whiteSpace: 'nowrap' }}>-{fmt(mm)}</td>
                            <td style={{ padding: '9px 14px', color: '#dc2626', whiteSpace: 'nowrap' }}>-{fmt(bolo)}</td>
                            <td style={{ padding: '9px 14px', color: '#dc2626', whiteSpace: 'nowrap' }}>-{fmt(health)}</td>
                            <td style={{ padding: '9px 14px', fontWeight: 800, color: '#1b4332', fontSize: 12, whiteSpace: 'nowrap' }}>{fmt(item.net_salary)}</td>
                            <td style={{ padding: '9px 14px', whiteSpace: 'nowrap' }}>
                              <span style={{
                                padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                                background: item.status === 'calculated' ? '#dcfce7' : '#fee2e2',
                                color: item.status === 'calculated' ? '#166534' : '#991b1b'
                              }}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Approval History */}
            {approvals.length > 0 && (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '16px 20px' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700 }}>Approval History</h3>
                {approvals.map(a => (
                  <div key={a.id} style={{ padding: '10px 14px', background: '#f8fafc', borderRadius: 6, marginBottom: 8, borderLeft: `3px solid ${actionColors[a.action] || '#94a3b8'}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontWeight: 700, fontSize: 13, color: actionColors[a.action] || '#374151', textTransform: 'capitalize' }}>{a.action}</span>
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(a.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>By: {a.user?.email}</div>
                    {a.remarks && <div style={{ fontSize: 12, color: '#374151', marginTop: 4, fontStyle: 'italic' }}>"{a.remarks}"</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalTab;
